import { Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, CommonModule } from '@angular/common';
import { ValidationComponent } from '../../validation/validation.component';
import { SearchRequestValidator } from './search-request.validator';
import { IRequestSearch } from '../models/search-request.model';
import { SearchRequestGridComponent } from "../search-request-grid/search-request-grid.component";
import { SearchRequestService } from '../services/search-request.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { RequestStatuses, requestStatusesMapping } from '../models/request-status.model';
import { IPerson } from '../../models/person.model';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { SearchParameters } from '../../shared/models/searchParameters.model';
import { PersonSearchComponent } from '../../shared/person-search/person-search.component';
import { Router } from '@angular/router';
import { LoggedInUserService } from '../../services/common/logged-in-user.service';

export type SearchFormControls = {
  [K in keyof IRequestSearch]: FormControl<string>;
};

@Component({
  selector: 'app-search-request',
  standalone: true,
  imports: [MatIconModule, 
    ReactiveFormsModule, 
    ValidationComponent, 
    CommonModule, 
    SearchRequestGridComponent, 
    MatFormField, 
    MatAutocompleteModule, 
    MatFormFieldModule, 
    MatInputModule, 
    FormsModule, 
    AsyncPipe,
    PersonSearchComponent],
  templateUrl: './search-request.component.html',
  styleUrl: './search-request.component.scss'
})

export class SearchRequestComponent {

  validationMessages = [];
  searchForm: FormGroup<SearchFormControls> = this.createSearchRequestForm();
  searchRequestService: SearchRequestService = inject(SearchRequestService);
  requestTypesList = toSignal(this.searchRequestService.getAllRequestTypes(), { rejectErrors: true });
  divisionsList = toSignal(this.searchRequestService.getAllDivisions(), { rejectErrors: true });
  requestStatusesList = Object.values(RequestStatuses);
  requestStatusesMapping = requestStatusesMapping;
  searchRequestData: IRequestSearch;
  @ViewChild(SearchRequestGridComponent) searchRequestGrid: SearchRequestGridComponent;

  constructor(
    private validator: SearchRequestValidator, 
    private fb: FormBuilder,
    private router: Router,
    private userService: LoggedInUserService) { }

  ngOnInit(): void {
    this.userService.currentUser$.subscribe(data => {
      if(data == null){
        this.router.navigate(["/unauthorized"]);
      }
    });
  }
  
  submitForm(): void {
    if (this.searchForm.valid) {
      this.validationMessages = [];
      this.searchRequestData = this.createSearchFormModel();
      let searchRequestParams = new SearchParameters<IRequestSearch>();
      searchRequestParams.pageIndex = 0;
      searchRequestParams.pageSize = 10;
      searchRequestParams.query = this.searchRequestData;
      this.searchRequestGrid.search(searchRequestParams);
      
    } else {
      if (this.searchForm.errors) {
        this.validationMessages.push(this.searchForm.errors?.atLeastOneFieldRequired);
      }
      if (this.searchForm.controls.employee.errors) {
        this.validationMessages.push(this.searchForm.controls.employee.errors.pattern);
      }
      if (this.searchForm.controls.approvedBy.errors) {
        this.validationMessages.push(this.searchForm.controls.approvedBy.errors.pattern);
      }
      if (this.searchForm.controls.waitingOn.errors) {
        this.validationMessages.push(this.searchForm.controls.waitingOn.errors.pattern);
      }
    }
  }

  private createSearchRequestForm(): FormGroup<SearchFormControls> {
    return this.fb.group<SearchFormControls>({
      requestFrom: new FormControl(''),
      requestTo: new FormControl(''),
      requestDateFrom: new FormControl(''),
      requestDateTo: new FormControl(''),
      requestType: new FormControl(''),
      requestTitle: new FormControl(''),
      division: new FormControl(''),
      chargeAfeCc: new FormControl(''),
      requestStatus: new FormControl(''),
      employee: new FormControl(''),
      approvedBy: new FormControl(''),
      waitingOn: new FormControl(''),
    }, 
    { 
      validators: [this.validator.atLeastOneFieldRequired()]
    });
  }

  private createSearchFormModel(): IRequestSearch {
    return {
      requestFrom: Number(this.searchForm.get('requestFrom').value),
      requestTo: Number(this.searchForm.get('requestTo').value),
      requestDateFrom: new Date(this.searchForm.get('requestDateFrom').value),
      requestDateTo: new Date(this.searchForm.get('requestDateTo').value),
      requestType: this.searchForm.get('requestType').value,
      requestTitle: this.searchForm.get('requestTitle').value,
      division: this.searchForm.get('division').value,
      chargeAfeCc: this.searchForm.get('chargeAfeCc').value,
      requestStatus: this.searchForm.get('requestStatus').value,
      employee: this.searchForm.get('employee').value,
      approvedBy: this.searchForm.get('approvedBy').value,
      waitingOn: this.searchForm.get('waitingOn').value
    };
  }

  onEmployeeSelected(person: IPerson){
    this.searchForm.get('employee').setValue(person.email.toString());
  }

  onEmployeeClear(){
    this.searchForm.get('employee').setValue('');
  }

  onApprovedBySelected(person: IPerson){
    this.searchForm.get('approvedBy').setValue(person.email.toString());
  }

  onApprovedByClear(){
    this.searchForm.get('approvedBy').setValue('');
  }

  onWaitingOnSelected(person: IPerson){
    this.searchForm.get('waitingOn').setValue(person.email.toString());
  }

  onWaitingOnClear(){
    this.searchForm.get('waitingOn').setValue('');
  }     
}